import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Building2,
  Search,
  UserCog,
  Briefcase,
  Loader2,
  AlertCircle,
  Monitor,
  Eye,
  EyeOff,
  Store,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { usersService, User } from "@/services/usersService";
import { getDevices, type Device } from "@/services/devicesService";
import { getCompanies, type Company } from "@/services/companiesService";
import { getBranches, type Branch } from "@/services/branchesService";
import UserFormModal from "@/components/UserFormModal";
import { useCompanyPanels, CompanyPanels } from "@/components/CompanyPanels";
import { useBranchPanels, BranchPanels } from "@/components/BranchPanels";
import { useOwnerPanels, OwnerPanels } from "@/components/OwnerPanels";
import { useManagerPanels, ManagerPanels } from "@/components/ManagerPanels";
import { useDevicePanels, DevicePanels } from "@/components/DevicePanels";
import UserAvatar from "@/components/UserAvatar";

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState("owners");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);   // carga inicial
  const [isRefreshing, setIsRefreshing] = useState(false); // recargas posteriores

  const [users, setUsers] = useState<User[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Managers filter state
  const [filterCompanyId, setFilterCompanyId] = useState<string>("all");
  const [filterBranchId, setFilterBranchId] = useState<string>("all");
  const [filterCompanies, setFilterCompanies] = useState<Company[]>([]);
  const [filterBranches, setFilterBranches] = useState<Branch[]>([]);

  // Summary counts
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [allBranches, setAllBranches] = useState<Branch[]>([]);

  // User form modal state
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userModalRole, setUserModalRole] = useState<2 | 3>(2);

  const companyPanels = useCompanyPanels();
  const branchPanels = useBranchPanels();
  const ownerPanels = useOwnerPanels();
  const managerPanels = useManagerPanels();
  const devicePanels = useDevicePanels();
  const [branchFilterCompanyId, setBranchFilterCompanyId] = useState<string>("all");

  // "Mostrar inactivos" toggle para cada panel (por defecto solo activos)
  const [showInactiveOwners, setShowInactiveOwners] = useState(false);
  const [showInactiveManagers, setShowInactiveManagers] = useState(false);
  const [showInactiveDevices, setShowInactiveDevices] = useState(false);
  const [showInactiveCompanies, setShowInactiveCompanies] = useState(false);
  const [showInactiveBranches, setShowInactiveBranches] = useState(false);

  const loadData = async () => {
    // Primera carga: pantalla completa. Recargas: indicador sutil para no desmontar paneles.
    if (isLoading) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    try {
      const [allUsers, allDevices, companies, branches] = await Promise.all([
        usersService.getUsers(),
        getDevices(),
        getCompanies(),
        getBranches(),
      ]);
      setUsers(allUsers);
      setDevices(allDevices);
      setAllCompanies(companies);
      setAllBranches(branches);
      setFilterCompanies(companies);

    } catch (err) {
      const message = err instanceof Error ? err.message : "Error cargando datos";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setFilterBranchId("all");
    if (filterCompanyId && filterCompanyId !== "all") {
      getBranches(filterCompanyId)
        .then(setFilterBranches)
        .catch(() => setFilterBranches([]));
    } else {
      setFilterBranches([]);
    }
  }, [filterCompanyId]);

  // Filtrar usuarios por término de búsqueda (excluye sysadmins)
  const filteredUsers = users.filter((user) => {
    if (user.role_id === 1) return false; // ocultar sysadmins
    const searchLower = searchTerm.toLowerCase();
    const fullName = usersService.getFullName(user).toLowerCase();
    return (
      fullName.includes(searchLower) ||
      user.username.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.role_name.toLowerCase().includes(searchLower)
    );
  });

  // Separar usuarios por tipo de rol
  const owners = filteredUsers
    .filter(u => u.role_id === 2)
    .filter(u => showInactiveOwners || u.is_active);

  // Filtrar gerentes por compañía y sucursal seleccionadas
  const managers = users
    .filter(u => u.role_id === 3)
    .filter(u => filterCompanyId === "all" || u.company_id === filterCompanyId)
    .filter(u => filterBranchId === "all" || u.branch_id === filterBranchId)
    .filter(u => showInactiveManagers || u.is_active);

  // Filtrar dispositivos por término de búsqueda
  const filteredDevices = devices.filter((d) => {
    if (!showInactiveDevices && !d.is_active) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      d.name.toLowerCase().includes(s) ||
      d.username.toLowerCase().includes(s) ||
      d.company_name.toLowerCase().includes(s) ||
      d.branch_name.toLowerCase().includes(s)
    );
  });

  // Filtrar empresas por término de búsqueda
  const filteredCompanies = allCompanies.filter((c) => {
    if (!showInactiveCompanies && !c.is_active) return false;
    if (!searchTerm) return true;
    return c.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Filtrar sucursales por empresa seleccionada y término de búsqueda
  const filteredBranches = allBranches
    .filter((b) => showInactiveBranches || b.is_active)
    .filter((b) => branchFilterCompanyId === "all" || b.company_id === branchFilterCompanyId)
    .filter((b) => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return b.name.toLowerCase().includes(s) || b.company_name.toLowerCase().includes(s);
    });

  // Handler: Activar/Desactivar usuario
  const handleToggleUserActive = async (user: User) => {
    console.log(`🔄 Toggle user ${user.id} - Current state: ${user.is_active}`);
    
    try {
      if (user.is_active) {
        await usersService.deactivateUser(user.id);
        console.log(`✅ User ${user.id} deactivated`);
      } else {
        await usersService.activateUser(user.id);
        console.log(`✅ User ${user.id} activated`);
      }
      
      // Actualizar lista local
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, is_active: !u.is_active } : u
        )
      );
      
      toast.success(user.is_active ? "Usuario desactivado" : "Usuario activado");
    } catch (error) {
      console.error("❌ Error toggling user:", error);
      toast.error("Error al actualizar el usuario");
    }
  };

  // Handler: Editar usuario (roles 2 y 3)
  const handleEditUser = (user: User) => {
    if (user.role_id === 2 || user.role_id === 3) {
      setSelectedUser(user);
      setUserModalRole(user.role_id as 2 | 3);
      setIsUserModalOpen(true);
    } else {
      toast.info(`Editar usuario: ${usersService.getFullName(user)}`);
    }
  };

  // Handler: Crear usuario según el rol activo
  const handleCreateUser = (roleId: 2 | 3) => {
    setSelectedUser(null);
    setUserModalRole(roleId);
    setIsUserModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-white/60">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s" }} />
      </div>

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/5 border-b border-white/10" />

      <main className="container py-6 relative z-10">
        {/* Indicador de recarga sutil — no desmonta ningún panel */}
        {isRefreshing && (
          <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-slate-800/90 backdrop-blur border border-white/10 rounded-full px-3 py-1.5 shadow-lg text-xs text-white/60">
            <Loader2 className="h-3 w-3 animate-spin text-blue-400" />
            Actualizando…
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-4 mb-6">
          {/* Empresas */}
          <div
            onClick={() => setActiveTab("companies")}
            className={`sm:w-44 sm:flex-none backdrop-blur-xl rounded-2xl p-4 border transition-all duration-300 cursor-pointer ${
              activeTab === "companies"
                ? "bg-white/20 border-purple-400/50 ring-2 ring-purple-400/30 shadow-lg shadow-purple-500/20 scale-[1.02]"
                : "bg-white/10 border-white/20 hover:bg-white/15 hover:scale-[1.02] hover:shadow-lg hover:shadow-purple-500/10"
            }`}
          >
            <div className="flex items-center gap-3">
              <UserAvatar role="company" size="md" icon={<Building2 className="h-5 w-5" />} />
              <div>
                <p className="text-2xl font-bold text-white">{allCompanies.length}</p>
                <p className="text-xs text-white/60">Empresas</p>
              </div>
            </div>
          </div>
          {/* Sucursales */}
          <div
            onClick={() => setActiveTab("branches")}
            className={`sm:w-44 sm:flex-none backdrop-blur-xl rounded-2xl p-4 border transition-all duration-300 cursor-pointer ${
              activeTab === "branches"
                ? "bg-white/20 border-amber-400/50 ring-2 ring-amber-400/30 shadow-lg shadow-amber-500/20 scale-[1.02]"
                : "bg-white/10 border-white/20 hover:bg-white/15 hover:scale-[1.02] hover:shadow-lg hover:shadow-amber-500/10"
            }`}
          >
            <div className="flex items-center gap-3">
              <UserAvatar role="branch" size="md" icon={<Store className="h-5 w-5" />} />
              <div>
                <p className="text-2xl font-bold text-white">{allBranches.length}</p>
                <p className="text-xs text-white/60">Sucursales</p>
              </div>
            </div>
          </div>
          {/* Dueños */}
          <div
            onClick={() => setActiveTab("owners")}
            className={`sm:w-44 sm:flex-none backdrop-blur-xl rounded-2xl p-4 border transition-all duration-300 cursor-pointer ${
              activeTab === "owners"
                ? "bg-white/20 border-blue-400/50 ring-2 ring-blue-400/30 shadow-lg shadow-blue-500/20 scale-[1.02]"
                : "bg-white/10 border-white/20 hover:bg-white/15 hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-500/10"
            }`}
          >
            <div className="flex items-center gap-3">
              <UserAvatar role="owner" size="md" icon={<Briefcase className="h-5 w-5" />} />
              <div>
                <p className="text-2xl font-bold text-white">{users.filter(u => u.role_id === 2).length}</p>
                <p className="text-xs text-white/60">Dueños</p>
              </div>
            </div>
          </div>
          {/* Gerentes */}
          <div
            onClick={() => setActiveTab("managers")}
            className={`sm:w-44 sm:flex-none backdrop-blur-xl rounded-2xl p-4 border transition-all duration-300 cursor-pointer ${
              activeTab === "managers"
                ? "bg-white/20 border-emerald-400/50 ring-2 ring-emerald-400/30 shadow-lg shadow-emerald-500/20 scale-[1.02]"
                : "bg-white/10 border-white/20 hover:bg-white/15 hover:scale-[1.02] hover:shadow-lg hover:shadow-emerald-500/10"
            }`}
          >
            <div className="flex items-center gap-3">
              <UserAvatar role="manager" size="md" icon={<UserCog className="h-5 w-5" />} />
              <div>
                <p className="text-2xl font-bold text-white">{users.filter(u => u.role_id === 3).length}</p>
                <p className="text-xs text-white/60">Gerentes</p>
              </div>
            </div>
          </div>
          {/* Dispositivos */}
          <div
            onClick={() => setActiveTab("devices")}
            className={`sm:w-44 sm:flex-none backdrop-blur-xl rounded-2xl p-4 border transition-all duration-300 cursor-pointer ${
              activeTab === "devices"
                ? "bg-white/20 border-cyan-400/50 ring-2 ring-cyan-400/30 shadow-lg shadow-cyan-500/20 scale-[1.02]"
                : "bg-white/10 border-white/20 hover:bg-white/15 hover:scale-[1.02] hover:shadow-lg hover:shadow-cyan-500/10"
            }`}
          >
            <div className="flex items-center gap-3">
              <UserAvatar role="device" size="md" icon={<Monitor className="h-5 w-5" />} />
              <div>
                <p className="text-2xl font-bold text-white">{devices.length}</p>
                <p className="text-xs text-white/60">Dispositivos</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex flex-col gap-4">
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  if (activeTab === "devices") {
                    devicePanels.openCreate();
                  } else if (activeTab === "companies") {
                    companyPanels.openCreate();
                  } else if (activeTab === "branches") {
                    branchPanels.openCreate();
                  } else if (activeTab === "owners") {
                    ownerPanels.openCreate();
                  } else if (activeTab === "managers") {
                    managerPanels.openCreate();
                  } else {
                    handleCreateUser(3);
                  }
                }}
                className="gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/25"
              >
                <Plus className="h-4 w-4" />
                {activeTab === "owners" ? "Nuevo Dueño"
                  : activeTab === "managers" ? "Nuevo Gerente"
                  : activeTab === "devices" ? "Nuevo Dispositivo"
                  : activeTab === "companies" ? "Nueva Empresa"
                  : "Nueva Sucursal"}
              </Button>
            </div>

            <div className="flex items-start gap-3">
              {/* Filtros principales (búsqueda o selects) */}
              <div className="relative flex-1">
                {activeTab === "managers" ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Select
                      value={filterCompanyId}
                      onValueChange={setFilterCompanyId}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/15 focus:border-white/30 [&>span]:text-white/60 data-[placeholder]:text-white/40">
                        <SelectValue placeholder="Todas las empresas" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/20 text-white">
                        <SelectItem value="all" className="text-white/70 focus:bg-white/10 focus:text-white">Todas las empresas</SelectItem>
                        {filterCompanies.map(c => (
                          <SelectItem key={c.id} value={c.id} className="text-white focus:bg-white/10 focus:text-white">{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={filterBranchId}
                      onValueChange={setFilterBranchId}
                      disabled={filterCompanyId === "all"}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/15 focus:border-white/30 disabled:opacity-40 [&>span]:text-white/60 data-[placeholder]:text-white/40">
                        <SelectValue placeholder="Todas las sucursales" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-white/20 text-white">
                        <SelectItem value="all" className="text-white/70 focus:bg-white/10 focus:text-white">Todas las sucursales</SelectItem>
                        {filterBranches.map(b => (
                          <SelectItem key={b.id} value={b.id} className="text-white focus:bg-white/10 focus:text-white">{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : activeTab === "branches" ? (
                  <Select
                    value={branchFilterCompanyId}
                    onValueChange={setBranchFilterCompanyId}
                  >
                    <SelectTrigger className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/15 focus:border-white/30 [&>span]:text-white/60 data-[placeholder]:text-white/40">
                      <SelectValue placeholder="Todas las empresas" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/20 text-white">
                      <SelectItem value="all" className="text-white/70 focus:bg-white/10 focus:text-white">Todas las empresas</SelectItem>
                      {filterCompanies.map(c => (
                        <SelectItem key={c.id} value={c.id} className="text-white focus:bg-white/10 focus:text-white">{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <>
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                    <Input
                      placeholder={
                        activeTab === "owners"
                          ? "Buscar por nombre, email, usuario o empresa..."
                          : activeTab === "devices"
                          ? "Buscar por nombre, empresa o sucursal..."
                          : activeTab === "companies"
                          ? "Buscar empresa..."
                          : activeTab === "branches"
                          ? "Buscar sucursal o empresa..."
                          : "Buscar por nombre, email, empresa o sucursal..."
                      }
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/15 focus:border-white/30"
                    />
                  </>
                )}
              </div>

              {/* Toggle: mostrar / ocultar registros inactivos */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (activeTab === "owners") setShowInactiveOwners(v => !v);
                  else if (activeTab === "managers") setShowInactiveManagers(v => !v);
                  else if (activeTab === "devices") setShowInactiveDevices(v => !v);
                  else if (activeTab === "companies") setShowInactiveCompanies(v => !v);
                  else if (activeTab === "branches") setShowInactiveBranches(v => !v);
                }}
                className={
                  (activeTab === "owners" && showInactiveOwners) ||
                  (activeTab === "managers" && showInactiveManagers) ||
                  (activeTab === "devices" && showInactiveDevices) ||
                  (activeTab === "companies" && showInactiveCompanies) ||
                  (activeTab === "branches" && showInactiveBranches)
                    ? "flex-shrink-0 h-10 border-amber-500/40 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 hover:text-amber-300"
                    : "flex-shrink-0 h-10 border-white/20 text-white/50 bg-transparent hover:bg-white/10 hover:text-white"
                }
              >
                <EyeOff className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Inactivos</span>
              </Button>
            </div>
          </div>

          <TabsContent value="all" className="space-y-4">
            <div className="hidden md:block backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">ID</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Nombre</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Email</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Empresa</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Rol</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Estado</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-white/40">
                        {searchTerm ? "No se encontraron resultados" : "No hay usuarios registrados"}
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4 text-white/60 text-sm">#{user.id.substring(0, 8)}</td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white font-medium text-sm ${
                              user.role_id === 1 
                                ? "bg-gradient-to-br from-purple-500 to-pink-600" 
                                : "bg-gradient-to-br from-blue-500 to-cyan-600"
                            }`}>
                              {usersService.getInitials(user)}
                            </div>
                            <div>
                              <p className="font-medium text-white">{usersService.getFullName(user)}</p>
                              <p className="text-sm text-white/50">@{user.username}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-white/60">{user.email || "-"}</td>
                        <td className="py-4 px-4 text-white/80">{user.company_name || "-"}</td>
                        <td className="py-4 px-4">
                          <Badge variant={user.role_id === 1 ? "default" : "secondary"} className={user.role_id === 1 ? "bg-purple-500/20 text-purple-400 border-purple-500/30" : "bg-blue-500/20 text-blue-400 border-blue-500/30"}>
                            {user.role_name}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Switch
                            checked={user.is_active}
                            onCheckedChange={() => handleToggleUserActive(user)}
                            className="data-[state=checked]:bg-emerald-500"
                          />
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                            className="text-white/60 hover:text-white hover:bg-white/10"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-4">
              {filteredUsers.length === 0 ? (
                <div className="backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 p-8 text-center text-white/40">
                  {searchTerm ? "No se encontraron resultados" : "No hay usuarios registrados"}
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <div key={user.id} className="backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 p-4 space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-medium ${
                          user.role_id === 1
                            ? "bg-gradient-to-br from-purple-500 to-pink-600"
                            : "bg-gradient-to-br from-blue-500 to-cyan-600"
                        }`}>
                          {usersService.getInitials(user)}
                        </div>
                        <div>
                          <p className="font-medium text-white">{usersService.getFullName(user)}</p>
                          <p className="text-sm text-white/50">{user.email || "Sin email"}</p>
                        </div>
                      </div>
                      <Badge variant={user.is_active ? "default" : "secondary"} className={user.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                        {user.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <p className="text-white/60"><span className="text-white/40">Empresa:</span> {user.company_name || "-"}</p>
                      <p className="text-white/60"><span className="text-white/40">Rol:</span> {user.role_name}</p>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-white/40">Estado:</span>
                      
                        <Switch
                          checked={user.is_active}
                          onCheckedChange={() => handleToggleUserActive(user)}
                          className="data-[state=checked]:bg-emerald-500"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditUser(user)}
                        className="text-white/60 hover:text-white hover:bg-white/10"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Editar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="owners" className="space-y-4">
            <div className="hidden md:block backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Nombre</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Empresa</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Email</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Estado</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {owners.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-white/40">
                        {searchTerm ? "No se encontraron resultados" : "No hay dueños registrados"}
                      </td>
                    </tr>
                  ) : (
                    owners.map((owner) => (
                      <tr key={owner.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <UserAvatar initials={usersService.getInitials(owner)} role="owner" size="md" />
                            <div>
                              <p className="font-medium text-white">{usersService.getFullName(owner)}</p>
                              <p className="text-sm text-white/50">@{owner.username}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-white/80">{owner.company_name || "-"}</td>
                        <td className="py-4 px-4 text-white/60">{owner.email || "-"}</td>
                        <td className="py-4 px-4 text-center">
                          <Badge className={owner.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                            {owner.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => ownerPanels.openDetail(owner)}
                              className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Eye className="h-4 w-4" />
                              <span className="text-xs">Ver</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => ownerPanels.openEdit(owner)}
                              className="text-white/60 hover:text-white hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="text-xs">Editar</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-4">
              {owners.length === 0 ? (
                <div className="backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 p-8 text-center text-white/40">
                  {searchTerm ? "No se encontraron resultados" : "No hay dueños registrados"}
                </div>
              ) : (
                owners.map((owner) => (
                  <div key={owner.id} className="backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <UserAvatar initials={usersService.getInitials(owner)} role="owner" size="lg" />
                        <div>
                          <p className="font-medium text-white">{usersService.getFullName(owner)}</p>
                          <p className="text-sm text-white/50">{owner.company_name || "-"}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={owner.is_active ? "default" : "secondary"} className={owner.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                          {owner.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => ownerPanels.openDetail(owner)}
                            className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto p-1"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => ownerPanels.openEdit(owner)}
                            className="text-white/60 hover:text-white hover:bg-white/10 h-auto p-1"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="managers" className="space-y-4">
            <div className="hidden md:block backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Nombre</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Email</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Empresa</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Sucursal</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Estado</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {managers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-white/40">
                        {searchTerm ? "No se encontraron resultados" : "No hay gerentes registrados"}
                      </td>
                    </tr>
                  ) : (
                    managers.map((manager) => (
                      <tr key={manager.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <UserAvatar initials={usersService.getInitials(manager)} role="manager" size="md" />
                            <div>
                              <p className="font-medium text-white">{usersService.getFullName(manager)}</p>
                              <p className="text-sm text-white/50">@{manager.username}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-white/60">{manager.email || "-"}</td>
                        <td className="py-4 px-4 text-white/80">{manager.company_name || "-"}</td>
                        <td className="py-4 px-4 text-white/60">{manager.branch_name || "-"}</td>
                        <td className="py-4 px-4 text-center">
                          <Badge className={manager.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                            {manager.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => managerPanels.openDetail(manager)}
                              className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Eye className="h-4 w-4" />
                              <span className="text-xs">Ver</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => managerPanels.openEdit(manager)}
                              className="text-white/60 hover:text-white hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="text-xs">Editar</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-4">
              {managers.length === 0 ? (
                <div className="backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 p-8 text-center text-white/40">
                  {searchTerm ? "No se encontraron resultados" : "No hay gerentes registrados"}
                </div>
              ) : (
                managers.map((manager) => (
                  <div key={manager.id} className="backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <UserAvatar initials={usersService.getInitials(manager)} role="manager" size="lg" />
                        <div>
                          <p className="font-medium text-white">{usersService.getFullName(manager)}</p>
                          <p className="text-sm text-white/50">
                            {[manager.company_name, manager.branch_name].filter(Boolean).join(" / ") || "-"}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={manager.is_active ? "default" : "secondary"} className={manager.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                          {manager.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => managerPanels.openDetail(manager)}
                            className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto p-1"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => managerPanels.openEdit(manager)}
                            className="text-white/60 hover:text-white hover:bg-white/10 h-auto p-1"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="devices" className="space-y-4">
            {/* Tabla desktop */}
            <div className="hidden md:block backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Nombre</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Usuario</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Empresa</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Sucursal</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Última sync</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Estado</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-white/40">
                        {searchTerm ? "No se encontraron resultados" : "No hay dispositivos registrados"}
                      </td>
                    </tr>
                  ) : (
                    filteredDevices.map((device) => (
                      <tr key={device.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <UserAvatar role="device" size="md" icon={<Monitor className="h-5 w-5" />} />
                            <p className="font-medium text-white">{device.name}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-white/60 font-mono text-sm">{device.username}</td>
                        <td className="py-4 px-4 text-white/80">{device.company_name}</td>
                        <td className="py-4 px-4 text-white/60">{device.branch_name}</td>
                        <td className="py-4 px-4 text-white/50 text-sm">
                          {device.last_sync
                            ? new Date(device.last_sync).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
                            : <span className="text-white/30">Nunca</span>}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Badge className={device.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                            {device.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => devicePanels.openDetail(device)}
                              className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Eye className="h-4 w-4" />
                              <span className="text-xs">Ver</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => devicePanels.openEdit(device)}
                              className="text-white/60 hover:text-white hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="text-xs">Editar</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Cards mobile */}
            <div className="md:hidden space-y-4">
              {filteredDevices.length === 0 ? (
                <div className="backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 p-8 text-center text-white/40">
                  {searchTerm ? "No se encontraron resultados" : "No hay dispositivos registrados"}
                </div>
              ) : (
                filteredDevices.map((device) => (
                  <div key={device.id} className="backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <UserAvatar role="device" size="lg" icon={<Monitor className="h-6 w-6" />} />
                        <div>
                          <p className="font-medium text-white">{device.name}</p>
                          <p className="text-sm text-white/50 font-mono">{device.username}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={device.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                          {device.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => devicePanels.openDetail(device)}
                            className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto p-1"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => devicePanels.openEdit(device)}
                            className="text-white/60 hover:text-white hover:bg-white/10 h-auto p-1"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="text-white/60"><span className="text-white/40">Empresa:</span> {device.company_name}</p>
                      <p className="text-white/60"><span className="text-white/40">Sucursal:</span> {device.branch_name}</p>
                      <p className="text-white/60">
                        <span className="text-white/40">Última sync:</span>{" "}
                        {device.last_sync
                          ? new Date(device.last_sync).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
                          : <span className="text-white/30">Nunca</span>}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* ===== EMPRESAS ===== */}
          <TabsContent value="companies" className="space-y-4">
            <div className="hidden md:block backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Empresa</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Fecha de Alta</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Estado</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="py-12 text-center text-white/40">
                        {searchTerm ? "No se encontraron resultados" : "No hay empresas registradas"}
                      </td>
                    </tr>
                  ) : (
                    filteredCompanies.map((company) => (
                      <tr key={company.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white">
                              <Building2 className="h-5 w-5" />
                            </div>
                            <p className="font-medium text-white">{company.name}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-white/60">
                          {new Date(company.created_at).toLocaleDateString('es-MX', { dateStyle: 'medium' })}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Badge className={company.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                            {company.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => companyPanels.openDetail(company)}
                              className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Eye className="h-4 w-4" />
                              <span className="text-xs">Ver</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => companyPanels.openEdit(company)}
                              className="text-white/60 hover:text-white hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="text-xs">Editar</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-4">
              {filteredCompanies.length === 0 ? (
                <div className="backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 p-8 text-center text-white/40">
                  {searchTerm ? "No se encontraron resultados" : "No hay empresas registradas"}
                </div>
              ) : (
                filteredCompanies.map((company) => (
                  <div key={company.id} className="backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white flex-shrink-0">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-white truncate">{company.name}</p>
                          <p className="text-sm text-white/50">{new Date(company.created_at).toLocaleDateString('es-MX', { dateStyle: 'medium' })}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                        <Badge className={company.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                          {company.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => companyPanels.openDetail(company)}
                            className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto p-1"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => companyPanels.openEdit(company)}
                            className="text-white/60 hover:text-white hover:bg-white/10 h-auto p-1"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* ===== SUCURSALES ===== */}
          <TabsContent value="branches" className="space-y-4">
            <div className="hidden md:block backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Sucursal</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Empresa</th>
                    <th className="text-left py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Fecha de Alta</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Estado</th>
                    <th className="text-center py-4 px-4 text-xs font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBranches.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-white/40">
                        {searchTerm ? "No se encontraron resultados" : "No hay sucursales registradas"}
                      </td>
                    </tr>
                  ) : (
                    filteredBranches.map((branch) => (
                      <tr key={branch.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white">
                              <Store className="h-5 w-5" />
                            </div>
                            <p className="font-medium text-white">{branch.name}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-white/80">{branch.company_name || "-"}</td>
                        <td className="py-4 px-4 text-white/60">
                          {new Date(branch.created_at).toLocaleDateString('es-MX', { dateStyle: 'medium' })}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Badge className={branch.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                            {branch.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => branchPanels.openDetail(branch)}
                              className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Eye className="h-4 w-4" />
                              <span className="text-xs">Ver</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => branchPanels.openEdit(branch)}
                              className="text-white/60 hover:text-white hover:bg-white/10 h-auto px-2 py-1 gap-1.5"
                            >
                              <Pencil className="h-4 w-4" />
                              <span className="text-xs">Editar</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="md:hidden space-y-4">
              {filteredBranches.length === 0 ? (
                <div className="backdrop-blur-xl bg-white/5 rounded-2xl border border-white/10 p-8 text-center text-white/40">
                  {searchTerm ? "No se encontraron resultados" : "No hay sucursales registradas"}
                </div>
              ) : (
                filteredBranches.map((branch) => (
                  <div key={branch.id} className="backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white flex-shrink-0">
                          <Store className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-white truncate">{branch.name}</p>
                          <p className="text-sm text-white/50 truncate">{branch.company_name || "-"}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-2 flex-shrink-0">
                        <Badge className={branch.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-red-500/20 text-red-400 border-red-500/30"}>
                          {branch.is_active ? "Activo" : "Inactivo"}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => branchPanels.openDetail(branch)}
                            className="text-white/60 hover:text-cyan-400 hover:bg-white/10 h-auto p-1"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => branchPanels.openEdit(branch)}
                            className="text-white/60 hover:text-white hover:bg-white/10 h-auto p-1"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Modal de usuario (dueño / gerente) */}
      <UserFormModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        user={selectedUser}
        roleId={userModalRole}
        onSave={loadData}
      />

      {/* Paneles de sucursal (detalle + formulario) */}
      <BranchPanels hook={branchPanels} users={users} allCompanies={allCompanies} onSave={loadData} />

      {/* Paneles de dueño (detalle + formulario) */}
      <OwnerPanels hook={ownerPanels} allCompanies={allCompanies} onSave={loadData} />

      {/* Paneles de gerente (detalle + formulario) */}
      <ManagerPanels hook={managerPanels} allCompanies={allCompanies} allBranches={allBranches} onSave={loadData} />

      {/* Paneles de empresa (detalle + formulario) */}
      <CompanyPanels hook={companyPanels} users={users} onSave={loadData} />

      {/* Paneles de dispositivo (detalle + formulario) */}
      <DevicePanels hook={devicePanels} allCompanies={allCompanies} allBranches={allBranches} onSave={loadData} />
    </div>
  );
}
